export const BUYING_INTEL_EMPTY_VALUE = "--";

const DISPLAY_LOCALE = "de-DE";

const toFiniteNumber = value => {
    if (value === null || value === undefined || value === "") return undefined;

    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
};

export const formatBuyingIntelNumber = (
    value,
    { minimumFractionDigits = 0, maximumFractionDigits = minimumFractionDigits } = {}
) => {
    const number = toFiniteNumber(value);
    if (number === undefined) return BUYING_INTEL_EMPTY_VALUE;

    return new Intl.NumberFormat(DISPLAY_LOCALE, {
        minimumFractionDigits,
        maximumFractionDigits
    }).format(number);
};

export const formatBuyingIntelSignedNumber = (value, options) => {
    const number = toFiniteNumber(value);
    if (number === undefined) return BUYING_INTEL_EMPTY_VALUE;

    const formattedValue = formatBuyingIntelNumber(Math.abs(number), options);
    if (number === 0) return formattedValue;
    return `${number > 0 ? "+" : "-"}${formattedValue}`;
};

export const formatBuyingIntelDate = value => {
    if (typeof value !== "string") return BUYING_INTEL_EMPTY_VALUE;

    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!isoDateMatch) return value || BUYING_INTEL_EMPTY_VALUE;

    const [, year, month, day] = isoDateMatch;
    return `${day}/${month}/${year}`;
};

export const formatBuyingIntelPercentage = (value, { signed = false } = {}) => {
    const formatMethod = signed ? formatBuyingIntelSignedNumber : formatBuyingIntelNumber;
    const formattedValue = formatMethod(value, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
    });

    return formattedValue === BUYING_INTEL_EMPTY_VALUE ? formattedValue : `${formattedValue}%`;
};

export const buildBuyingIntelMetricCardData = ({
    plan,
    committedOrder,
    comparableSeason,
    unit = "",
    valueSuffix = "",
    minimumFractionDigits = 0,
    maximumFractionDigits = minimumFractionDigits,
    changeMode = "percentage",
    changeValue: providedChangeValue,
    changePercentage: providedChangePercentage,
    status
}) => {
    const valueOptions = { minimumFractionDigits, maximumFractionDigits };
    const planValue = toFiniteNumber(plan);
    const committedOrderValue = toFiniteNumber(committedOrder);
    const comparableSeasonValue = toFiniteNumber(comparableSeason);
    const changeSourceValue = committedOrderValue ?? planValue;
    const calculatedChangeValue =
        changeSourceValue === undefined || comparableSeasonValue === undefined
            ? undefined
            : changeSourceValue - comparableSeasonValue;
    const changeValue = toFiniteNumber(providedChangeValue) ?? calculatedChangeValue;
    const calculatedChangePercentage =
        changeValue === undefined || !comparableSeasonValue
            ? undefined
            : (changeValue / comparableSeasonValue) * 100;
    const changePercentage = toFiniteNumber(providedChangePercentage) ?? calculatedChangePercentage;

    const formatValue = value => {
        const formattedValue = formatBuyingIntelNumber(value, valueOptions);
        return formattedValue === BUYING_INTEL_EMPTY_VALUE ? formattedValue : `${formattedValue}${valueSuffix}`;
    };

    const formattedChangeValue = formatBuyingIntelSignedNumber(changeValue, valueOptions);
    const changeSuffix = changeMode === "percentagePoints" ? "pp" : valueSuffix;

    return {
        primary: {
            value: formatValue(planValue),
            unit
        },
        secondary:
            committedOrderValue === undefined
                ? undefined
                : {
                      value: formatValue(committedOrderValue),
                      unit
                  },
        tertiary: {
            value: formatValue(comparableSeasonValue),
            unit
        },
        change: {
            value:
                formattedChangeValue === BUYING_INTEL_EMPTY_VALUE
                    ? formattedChangeValue
                    : `${formattedChangeValue}${changeSuffix}`,
            unit: changeMode === "percentagePoints" ? "" : unit,
            percentage:
                changeMode === "percentage" && changePercentage !== undefined
                    ? formatBuyingIntelPercentage(changePercentage, { signed: true })
                    : undefined
        },
        status
    };
};

export const parseBuyingIntelAmount = value => {
    if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
    if (typeof value !== "string") return undefined;

    const normalizedValue = value.trim().replace(/\s/g, "").replace(/\.(?=\d{3}(?:\D|$))/g, "").replace(",", ".");
    const number = Number(normalizedValue);
    return Number.isFinite(number) ? number : undefined;
};
